import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useNotification } from '../hooks/NotificationContext';
import rechnungenService from '../services/rechnungenService';
import { kundenService } from '../services/kundenService';
import { Kunde, RechnungTextvorlage } from '../types';

const emptyForm = (): Omit<RechnungTextvorlage, 'id'> => ({
  name: '',
  is_default: false,
  anrede: '',
  anschreiben: '',
  grussformel: '',
  unterzeichner: '',
  leistungsnachweis_hinweis: '',
  zahlungshinweis: '',
  kunde_ids: [],
});

const RechnungTextvorlagenPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showSuccess, showError } = useNotification();

  const [vorlagen, setVorlagen] = useState<RechnungTextvorlage[]>([]);
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<number | 'neu' | null>(null);
  const [form, setForm] = useState<Omit<RechnungTextvorlage, 'id'>>(emptyForm());
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = async (selectId?: number) => {
    try {
      setLoading(true);
      const [liste, kundenListe] = await Promise.all([
        rechnungenService.listTextvorlagen(),
        kundenService.list(),
      ]);
      setVorlagen(liste);
      setKunden(kundenListe);
      const next = selectId ?? liste.find((v) => v.is_default)?.id ?? liste[0]?.id ?? null;
      setSelectedId(next);
      const current = liste.find((v) => v.id === next);
      setForm(current ? { ...current } : emptyForm());
    } catch {
      showError('Textvorlagen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const selectVorlage = (vorlage: RechnungTextvorlage) => {
    setSelectedId(vorlage.id);
    setForm({ ...vorlage });
  };

  const startNeu = () => {
    setSelectedId('neu');
    setForm(emptyForm());
  };

  const toggleKunde = (kundeId: number) => {
    setForm((prev) => ({
      ...prev,
      kunde_ids: prev.kunde_ids.includes(kundeId)
        ? prev.kunde_ids.filter((id) => id !== kundeId)
        : [...prev.kunde_ids, kundeId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showError('Bitte einen Namen für die Vorlage angeben.');
      return;
    }
    try {
      setSaving(true);
      if (selectedId === 'neu') {
        const created = await rechnungenService.createTextvorlage(form);
        showSuccess('Textvorlage erstellt');
        await load(created.id);
      } else if (typeof selectedId === 'number') {
        const updated = await rechnungenService.updateTextvorlage(selectedId, form);
        showSuccess('Textvorlage gespeichert');
        await load(updated.id);
      }
    } catch {
      // Interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (typeof selectedId !== 'number') return;
    try {
      await rechnungenService.deleteTextvorlage(selectedId);
      showSuccess('Textvorlage gelöscht');
      setDeleteOpen(false);
      await load();
    } catch {
      setDeleteOpen(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          title="Rechnungsvorlagen"
          subtitle="Textbausteine für Ausgangsrechnungen und E-Rechnungen"
          breadcrumbs={[{ label: 'Rechnungen', path: '/rechnungen' }, { label: 'Vorlagen' }]}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Rechnungsvorlagen"
        subtitle="Benannte Textbausteine für Kunden oder Kundengruppen"
        breadcrumbs={[{ label: 'Rechnungen', path: '/rechnungen' }, { label: 'Vorlagen' }]}
      />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Card sx={{ width: 320, flexShrink: 0 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Vorlagen</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={startNeu}>Neu</Button>
            </Box>
            <List dense>
              {vorlagen.map((vorlage) => (
                <ListItemButton
                  key={vorlage.id}
                  selected={selectedId === vorlage.id}
                  onClick={() => selectVorlage(vorlage)}
                >
                  <ListItemText
                    primary={vorlage.name}
                    secondary={vorlage.kunde_ids.length ? `${vorlage.kunde_ids.length} Kunden` : 'Keine Kundenzuordnung'}
                  />
                  {vorlage.is_default && <Chip size="small" label="Standard" />}
                </ListItemButton>
              ))}
            </List>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, minWidth: 320 }}>
          <CardContent>
            {selectedId == null ? (
              <Alert severity="info">Legen Sie eine erste Textvorlage an.</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_default}
                      onChange={(e) => setForm((prev) => ({ ...prev, is_default: e.target.checked }))}
                    />
                  }
                  label="Standardvorlage für alle Rechnungen ohne eigene Zuordnung"
                />
                <TextField
                  label="Anrede"
                  value={form.anrede}
                  onChange={(e) => setForm((prev) => ({ ...prev, anrede: e.target.value }))}
                />
                <TextField
                  label="Anschreiben"
                  multiline
                  minRows={3}
                  value={form.anschreiben}
                  onChange={(e) => setForm((prev) => ({ ...prev, anschreiben: e.target.value }))}
                  helperText="Eine Leerzeile trennt Absätze."
                />
                <TextField
                  label="Hinweis auf den Leistungsnachweis"
                  multiline
                  minRows={2}
                  value={form.leistungsnachweis_hinweis}
                  onChange={(e) => setForm((prev) => ({ ...prev, leistungsnachweis_hinweis: e.target.value }))}
                />
                <TextField
                  label="Zahlungshinweis"
                  multiline
                  minRows={2}
                  value={form.zahlungshinweis}
                  onChange={(e) => setForm((prev) => ({ ...prev, zahlungshinweis: e.target.value }))}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Grußformel"
                    fullWidth
                    value={form.grussformel}
                    onChange={(e) => setForm((prev) => ({ ...prev, grussformel: e.target.value }))}
                  />
                  <TextField
                    label="Unterzeichner"
                    fullWidth
                    value={form.unterzeichner}
                    onChange={(e) => setForm((prev) => ({ ...prev, unterzeichner: e.target.value }))}
                  />
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                  Gilt für diese Kunden
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Mehrere Kunden mit derselben Vorlage bilden eine Kundengruppe. Ohne Zuordnung gilt nur die Standardvorlage.
                </Typography>
                <Box
                  sx={{
                    maxHeight: 220,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1,
                    background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  {kunden.map((kunde) => (
                    <FormControlLabel
                      key={kunde.id}
                      sx={{ display: 'flex' }}
                      control={
                        <Checkbox
                          checked={form.kunde_ids.includes(kunde.id)}
                          onChange={() => toggleKunde(kunde.id)}
                        />
                      }
                      label={kunde.name_des_unternehmens}
                    />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={selectedId === 'neu' || vorlagen.length < 2}
                    onClick={() => setDeleteOpen(true)}
                  >
                    Löschen
                  </Button>
                  <Button variant="contained" disabled={saving} onClick={handleSave}>
                    {saving ? <CircularProgress size={22} /> : 'Speichern'}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        title="Textvorlage löschen"
        message="Diese Vorlage wirklich löschen? Zugewiesene Kunden fallen auf die Standardvorlage zurück."
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default RechnungTextvorlagenPage;
